// import React from 'react';
// import { navItems } from '.';
// import mira from 'assets/mira.svg';
// import { Link } from 'react-router-dom';
// import { useSelector } from 'react-redux';
// import { userRoleData } from 'redux/slices/userRole';

// /**
//  * Renders a navigation bar component.
//  *
//  * @returns {JSX.Element} The rendered navigation bar component.
//  */
// const NavBar = () => {
//   const role = useSelector(userRoleData);

//   const filteredNavItems = navItems?.filter((item) =>
//     item?.role?.includes(role?.userRole)
//   );
//   return (
//         // <div className="flex flex-col w-[88px] h-screen bg-gradient-to-b from-[#011f71] to-[#0e60fb] transition-all duration-300 ease-in-out">
// <div className="flex flex-col w-[88px] h-screen" style={{ background: 'linear-gradient(180deg, #0F1434 0%, #0630AD 64.5%, #0E61FE 100%)' }}>

//     {/* // <div className="flex flex-col w-[88px] secondary-bg h-screen"> */}
//       {/* <div className="flex justify-center mt-4 mb-6">
//         <div className="flex justify-center primary-bg rounded-full w-[36px] h-[36px]  text-white">
//           <img className="w-[26px]" src={mira} alt="mira" />
//         </div>
//       </div> */}

//       <nav className="flex-1 overflow-y-auto scroll-hidden">
//         {filteredNavItems?.map((item, index) => (
//           <NavItem
//             key={index}
//             title={item.title}
//             icon={item.icon}
//             path={item.path}
//             subPaths={item.subPaths}
//           />
//         ))}
//       </nav>
//     </div>
//   );
// };

// const NavItem = ({ title, icon, path, subPaths }) => {
//   return (
//     <Link
//       to={path}
//       className={`flex flex-col items-center p-4  ${
//         location.pathname.endsWith(path) ||
//         subPaths?.some((path) => location.pathname.startsWith(path))
//           ? 'bg-[#073A86] transition duration-300 '
//           : 'hover:bg-[#073A86]'
//       }   `}
//     >
//       {icon && <div className="mb-2">{icon}</div>}
//       <span
//         className={`${
//           location.pathname.endsWith(path) ||
//           subPaths?.some((path) => location.pathname.startsWith(path))
//             ? 'font-medium-bold text-base_white text-center'
//             : 'font-medium text-base_white text-center'
//         }`}
//       >
//         {title}
//       </span>
//     </Link>
//   );
// };

// export default NavBar;

import { useEffect, useMemo, useRef, useState } from 'react';
import { navItems } from '.';
import { canAccessSection } from '../../utils/accessControl';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { buildAppPath } from '@/utils/tenant';
import { useSelector } from 'react-redux';
import { userRoleData } from 'redux/slices/userRole';
import { selectUser } from 'redux/slices/authSlice';
import { performLogout } from '@/utils/logout';
import { LogOut } from 'lucide-react';
import AccountQuickPanel from '@/components/account/AccountQuickPanel';
import styles from './nav-bar.module.css';

const NavBar = () => {
  const role = useSelector(userRoleData);
  const user = useSelector(selectUser);
  const navigate = useNavigate();
  const location = useLocation();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [isAccountOpen, setIsAccountOpen] = useState(false);
  const accountContainerRef = useRef(null);

  const filteredNavItems = useMemo(() => navItems?.filter((item) => {
    if (item.section) {
      return canAccessSection(item.section, user?.originalRole || user?.role);
    }

    if (item.role) {
      return item.role.includes(role?.userRole);
    }

    return true;
  }), [user?.originalRole, user?.role, role?.userRole]);

  const displayName =
    user?.name ||
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
    user?.email ||
    'Mira User';
  const displayEmail = user?.email || '';
  const initials = displayName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || 'MU';
  const avatarUrl =
    user?.profile_picture_url ||
    user?.profilePictureUrl ||
    user?.avatar_url ||
    user?.profile_picture ||
    '';
  const isConsumer =
    String(user?.originalRole || user?.role || '').toLowerCase() === 'consumer';

  const isActiveRoute = (path, subPaths = []) => {
    const currentPath = location.pathname;
    if (currentPath === path || currentPath.startsWith(path)) {
      return true;
    }
    return subPaths.some((subPath) => currentPath.startsWith(subPath));
  };

  // B2C pages that should carry the active application ID in the URL
  const b2cAppIdPaths = ['/profile', '/documents'];
  const buildNavLink = (path) => {
    const base = buildAppPath(path);
    if (b2cAppIdPaths.includes(path)) {
      const appId = sessionStorage.getItem('b2c_active_application_id');
      if (appId) return `${base}?applicationId=${encodeURIComponent(appId)}`;
    }
    return base;
  };

  const isLongRunningTaskActive = false;

  useEffect(() => {
    const handleClickAway = (event) => {
      if (
        accountContainerRef.current &&
        !accountContainerRef.current.contains(event.target)
      ) {
        setIsAccountOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickAway);
    return () => document.removeEventListener('mousedown', handleClickAway);
  }, []);

  const handleSignOut = async () => {
    if (isLongRunningTaskActive) {
      setShowLogoutConfirm(true);
      return;
    }
    await performLogout({ redirect: true });
  };

  return (
    <>
      {isAccountOpen ? (
        <button
          type="button"
          aria-label="Close account popup"
          className={styles.accountBackdrop}
          onClick={() => setIsAccountOpen(false)}
        />
      ) : null}

      <aside className={`${styles.sidebar} ${isAccountOpen ? styles.sidebarAccountOpen : ''}`}>
        <div className={styles.sidebarInner}>
          <div className={styles.logoRow}>
            <span className={styles.brandIconWrapper}>
              <img src="/loaninghub-mark.png" alt="" className={styles.brandIcon} />
            </span>
            <img
              src="/loaninghub-logo-white.svg"
              alt="Loaning Hub"
              className={styles.brandWordmark}
            />
          </div>

          <nav className={styles.navList}>
            {filteredNavItems?.map((item) => {
              const active = isActiveRoute(item.path, item.subPaths || []);
              return (
                <Link
                  key={item.title}
                  to={buildNavLink(item.path)}
                  aria-label={item.title}
                  aria-current={active ? 'page' : undefined}
                  className={`${styles.actionButton} ${active ? styles.actionButtonActive : styles.actionButtonInactive}`}
                >
                  <div className={styles.navIconWrapper}>
                    {item.icon && (
                      <span
                        className={`${styles.navIcon} ${active ? styles.navIconActive : styles.navIconInactive}`}
                      >
                        {item.icon}
                      </span>
                    )}
                  </div>
                  <span className={`${styles.navLabel} ${active ? styles.navLabelActive : ''}`}>
                    {item.title}
                  </span>
                  <div className={styles.tooltip}>
                    {item.title}
                    <div className={styles.tooltipArrow} />
                  </div>
                </Link>
              );
            })}
          </nav>

          <div className={styles.footer}>
            <div ref={accountContainerRef} className={styles.accountPanelAnchor}>
              <button
                type="button"
                className={`${styles.profileRow} ${styles.profileRowButton}`}
                onClick={() => setIsAccountOpen((prev) => !prev)}
                aria-label="Open account panel"
                aria-expanded={isAccountOpen}
              >
                <div className={styles.profileAvatar}>
                  {avatarUrl ? (
                    <img
                      src={avatarUrl}
                      alt=""
                      className={styles.profileAvatarImage}
                    />
                  ) : (
                    initials
                  )}
                </div>
                <div className={styles.profileText}>
                  <p className={styles.profileName}>{displayName}</p>
                  {displayEmail ? <p className={styles.profileEmail}>{displayEmail}</p> : null}
                </div>
              </button>

              {isAccountOpen ? (
                <div
                  className={isConsumer ? styles.accountPanelPopover : styles.accountPanelModal}
                >
                  <AccountQuickPanel
                    user={user}
                    surface={isConsumer ? 'popover' : 'modal'}
                    onClose={() => setIsAccountOpen(false)}
                    onNotifications={() => {
                      setIsAccountOpen(false);
                      window.dispatchEvent(new CustomEvent('mira:open-notifications'));
                    }}
                    onSupport={() => {
                      setIsAccountOpen(false);
                      const isConsumer = String(user?.originalRole || user?.role || '').toLowerCase() === 'consumer';
                      navigate(buildAppPath(isConsumer ? '/support-docs' : '/help-support'));
                    }}
                  />
                </div>
              ) : null}
            </div>

            <button
              type="button"
              className={styles.signOutButton}
              onClick={handleSignOut}
              aria-label="Sign out"
            >
              <LogOut className={styles.lucideIcon} />
              <span className={styles.navLabel}>Sign out</span>
              <div className={styles.tooltip}>
                Sign out
                <div className={styles.tooltipArrow} />
              </div>
            </button>
          </div>
        </div>
      </aside>

      {showLogoutConfirm && (
        <div className={styles.logoutModalBackdrop}>
          <div className={styles.logoutModal}>
            <h3>Uploads in progress</h3>
            <p>
              A long-running upload is active. Signing out now may interrupt the process.
            </p>
            <div className={styles.logoutModalActions}>
              <button type="button" onClick={() => setShowLogoutConfirm(false)} className={styles.modalSecondary}>
                Cancel
              </button>
              <button
                type="button"
                onClick={() => performLogout({ redirect: true })}
                className={styles.modalPrimary}
              >
                Sign out anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default NavBar;
